import { memo, useCallback, useMemo } from 'react';
// import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { UserX } from 'lucide-react';

import { useAgoraEngine } from '@providers/AgoraEngineProvider';
// import { TeamType } from '@typings/participant';
import { ParticipantPosition } from '@typings/participant';
import { useParticipantDataById } from '@redux/selectors/participants';
import {
  useSpeakerLikeStatus,
  useSpeakerLikesCount,
} from '@redux/selectors/likes';
import Modal from '@components/modal';
import { useModal } from '@hooks/useModal';
import { useSpeakerDataById } from '@redux/selectors/speakers';
import { useUserDataById } from '@redux/selectors/users';
import { useLikeSpeakerMutation } from '@redux/queries/like';
import { PremierText } from '@components/text';
import SkewedIconButton from '@components/buttons/SkewedIconButton';
import { SKEW_ANGLE } from '@utils/constants';

import LeftSideParticipant from './LeftSideParticipant';
import RightSideParticipant from './RightSideParticipant';

const Container = styled.div`
  position: relative;
`;

const EmptyView = styled.div<{ $height: number; $width: number }>`
  width: ${({ $width, $height }) => $width + Math.ceil($height * 0.3)}px;
  height: ${({ $height }) => $height}px;
  background-color: transparent;
`;

const KickOffButtonWrapper = styled.div<{ $isLeft?: boolean }>`
  position: absolute;
  bottom: 40px;
  ${({ $isLeft }) => ($isLeft ? 'left: 16px;' : 'right: 16px;')}
  z-index: 2;
`;

const KickOffButton = styled(UserX)`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.red[300]};
  transform: skew(${SKEW_ANGLE}deg);
  cursor: pointer;
`;

type Props = {
  speakerId: string | null;
  position?: ParticipantPosition;
  size: {
    height: number;
    width: number;
  };
  speakingUIDs?: Record<string, number>;
  emojisSequence?: string[];
  isStatusesShown?: boolean;
  isRecord?: boolean;
  amISpeaker?: boolean;
  onEmojiFinish?: () => void;
};

const SpeakerView = ({
  speakerId,
  position,
  size,
  speakingUIDs = {},
  emojisSequence,
  isStatusesShown,
  isRecord,
  // amISpeaker,
  onEmojiFinish,
}: Props) => {
  const { isHost, currentShowId } = useAgoraEngine();
  const currentSpeaker = useSpeakerDataById(speakerId);
  const currentSpeakerProfileData = useUserDataById(currentSpeaker?.profileId);
  const participant = useParticipantDataById(currentSpeaker?.participantId);
  //   const navigate = useNavigate();

  const isSpeaking = useMemo(
    () =>
      (!!participant?.uid && speakingUIDs[participant.uid] !== undefined) ||
      (speakingUIDs['0'] === 0 && !isHost),
    [participant?.uid, speakingUIDs, isHost],
  );
  const isLiked = useSpeakerLikeStatus({ showId: currentShowId, speakerId });
  const likesCount = useSpeakerLikesCount({ showId: currentShowId, speakerId });
  // const [removeSpeakerFromShow] = useLeaveShowMutation();
  const [like] = useLikeSpeakerMutation();
  const { isModalShown, hideModal, showModal } = useModal();

  const sendSetLikeRequest = useCallback(
    (liked: boolean) => {
      if (!currentSpeaker?.profileId || !speakerId || !currentShowId) {
        return;
      }

      like({
        showId: currentShowId,
        userId: currentSpeaker?.profileId,
        speakerId,
        weight: liked ? 1 : 0,
      });
    },
    [currentSpeaker?.profileId, speakerId, currentShowId, like],
  );

  if (!speakerId) {
    return <EmptyView $height={size.height} $width={size.width} />;
  }

  return (
    <Container>
      {position === ParticipantPosition.Right ? (
        <RightSideParticipant
          key={participant?.id}
          participantId={participant?.id || null}
          side={participant?.team || currentSpeaker?.side}
          size={size}
          emojisSequence={emojisSequence}
          isSpeaking={isSpeaking}
          isStatusesShown={isStatusesShown}
          isLiked={isLiked}
          likesCount={likesCount}
          isRecord={isRecord}
          onLike={sendSetLikeRequest}
          onEmojiFinish={onEmojiFinish}
        />
      ) : (
        <LeftSideParticipant
          key={participant?.id}
          participantId={participant?.id || null}
          side={participant?.team || currentSpeaker?.side}
          size={size}
          emojisSequence={emojisSequence}
          isSpeaking={isSpeaking}
          isStatusesShown={isStatusesShown}
          isLiked={isLiked}
          likesCount={likesCount}
          isRecord={isRecord}
          onLike={sendSetLikeRequest}
          onEmojiFinish={onEmojiFinish}
        />
      )}

      {isHost && !isRecord && (
        <KickOffButtonWrapper $isLeft={position === ParticipantPosition.Left}>
          <SkewedIconButton
            icon={<KickOffButton />}
            onClick={() => showModal()}
          />
        </KickOffButtonWrapper>
      )}

      <Modal
        isShown={isModalShown}
        title='Kick off guest'
        isCloseable
        onClose={hideModal}
        onConfirm={() => {
          hideModal();
        }}
      >
        <PremierText>
          Are you sure you want to kick off{' '}
          {currentSpeakerProfileData?.name || 'the guest'}?
        </PremierText>
      </Modal>
    </Container>
  );
};

export default memo(SpeakerView);
